<template>
  <b-card v-if="hasClientCases || isOwner" class="py-4">
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Icon name="solar:case-round-bold-duotone" size="1.5rem" class="text-white" />
          <span class="text-white text-lg font-medium">
            {{ t('profile_client_cases.title') }}
          </span>
        </div>
        <b-button v-if="isOwner" light size="sm" @click="openEdit">
          <Icon name="solar:pen-bold-duotone" size="1rem" />
        </b-button>
      </div>

      <div class="flex flex-col gap-3 px-1">
        <div v-if="hasClientCases">
          <div v-for="(item, index) in clientCases" :key="index" class="flex items-start gap-2 mb-2">
            <Icon name="solar:link-bold" size="1rem" class="shrink-0 text-gray-400 mt-0.5" />
            <a
              :href="normalizeUrl(item.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-400 hover:text-blue-300 text-sm break-words min-w-0"
            >
              {{ item.title || displayUrl(item.url) }}
            </a>
          </div>
        </div>
        <p v-else-if="isOwner" class="text-gray-500 italic text-base">
          {{ t('profile_client_cases.empty') }}
        </p>
      </div>
    </div>
  </b-card>

  <b-modal v-model="modalIsOpen" :title="t('profile_client_cases.edit_title')" no-footer size="md">
    <div class="px-10 pb-8 flex flex-col gap-5">
      <div v-for="(entry, index) in editForm" :key="index" class="flex flex-col gap-3 border border-[#373737] rounded-lg p-4">
        <div class="flex items-center justify-between">
          <span class="text-gray-400 text-sm">{{ index + 1 }}</span>
          <button
            class="text-red-400 hover:text-red-300 cursor-pointer"
            @click="removeEntry(index)"
          >
            <Icon name="solar:trash-bin-trash-bold-duotone" size="1rem" />
          </button>
        </div>
        <b-text-field
          v-model="entry.url"
          :label="t('profile_client_cases.url_label')"
          :placeholder="t('profile_client_cases.url_placeholder')"
          :name="'client_case_url_' + index"
        />
        <b-text-field
          v-model="entry.title"
          :label="t('profile_client_cases.title_label')"
          :placeholder="t('profile_client_cases.title_placeholder')"
          :name="'client_case_title_' + index"
        />
      </div>

      <div v-if="editForm.length < MAX_ENTRIES">
        <b-button light size="sm" @click="addEntry">
          <Icon name="solar:add-circle-bold-duotone" size="1rem" class="mr-1" />
          {{ t('profile_client_cases.add') }}
        </b-button>
      </div>
      <p v-else class="text-gray-500 text-sm italic">
        {{ t('profile_client_cases.max_reached') }}
      </p>

      <div class="flex gap-4 pt-2">
        <b-button block light @click="modalIsOpen = false">{{ t('common.cancel') }}</b-button>
        <b-button block @click="onSubmit" :loading="saving">{{ t('common.save') }}</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup lang="ts">
import BCard from './BCard.vue';
import { authStore } from '~/stores/auth';
import { useProfileStore } from '~/stores/profileStore';
import { useUserStore } from '~/stores/userStore';
import BButton from '~/components/forms/BButton.vue';
import BModal from '~/components/modal/BModal.vue';
import BTextField from '~/components/forms/BTextField.vue';
import { useAlerts } from '~/stores/alert';

const MAX_ENTRIES = 3;

const { t } = useI18n();
const alerts = useAlerts();

const store = authStore();
const profileStore = useProfileStore();
const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const { user: me } = storeToRefs(store);

const { isPreview } = useProfilePreviewMode();
const isOwner = computed(() => !isPreview.value && me.value && user.value && me.value.id === user.value.id);

const modalIsOpen = ref(false);
const saving = ref(false);

interface ClientCaseEntry {
  url: string;
  title: string;
}

const editForm = ref<ClientCaseEntry[]>([]);

const clientCases = computed(() => user.value?.profile?.client_cases || []);
const hasClientCases = computed(() => clientCases.value.length > 0);

function normalizeUrl(url: string): string {
  if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function openEdit() {
  editForm.value = clientCases.value.length > 0
    ? clientCases.value.map((c) => ({ url: c.url, title: c.title || '' }))
    : [{ url: '', title: '' }];
  modalIsOpen.value = true;
}

function addEntry() {
  if (editForm.value.length < MAX_ENTRIES) {
    editForm.value.push({ url: '', title: '' });
  }
}

function removeEntry(index: number) {
  editForm.value.splice(index, 1);
}

async function onSubmit() {
  saving.value = true;
  try {
    // Filter out entries with empty URLs
    const payload = editForm.value
      .filter((e) => e.url.trim() !== '')
      .map((e) => ({
        url: e.url.trim(),
        title: e.title.trim() || undefined,
      }));

    const value = payload.length > 0 ? payload : null;
    await profileStore.updateProfile(user.value!.id, { client_cases: value });

    if (user.value?.profile) {
      (user.value.profile as any).client_cases = value;
    }

    modalIsOpen.value = false;
  } catch {
    alerts.add({ type: 'danger', title: t('profile_client_cases.edit_title') });
  } finally {
    saving.value = false;
  }
}
</script>
